/**
 * sanitizeHtml
 * Strips dangerous markup (scripts, event handlers, etc.) from advisor-authored
 * rich text before it is rendered to prospects via dangerouslySetInnerHTML.
 */

import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's',
  'ul', 'ol', 'li', 'a', 'span', 'div',
  'h1', 'h2', 'h3', 'h4', 'blockquote', 'hr',
  'table', 'thead', 'tbody', 'tr', 'td', 'th', 'img',
];

DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
  if (node.nodeName !== 'A') return;
  // Rich-text links must leave the (possibly embedded) quiz frame.
  if (/^(https?:)?\/\//i.test(node.getAttribute('href') || '')) node.setAttribute('target', '_blank');
  if (node.getAttribute('target') !== '_blank') return;

  const relTokens = new Set((node.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
  relTokens.add('noopener');
  relTokens.add('noreferrer');
  node.setAttribute('rel', Array.from(relTokens).join(' '));
});

export function sanitizeHtml(html: string | undefined | null): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'src', 'alt', 'width', 'height'],
  });
}

/** Return render-ready HTML only when sanitized rich text has visible content. */
export function sanitizeMeaningfulHtml(html: string | undefined | null): string {
  const sanitized = sanitizeHtml(html);
  if (!sanitized) return '';

  const { body } = new DOMParser().parseFromString(sanitized, 'text/html');
  const text = (body.textContent || '').replace(/[\s\u200B-\u200D\u2060\uFEFF]/gu, '');
  const hasImage = Array.from(body.querySelectorAll('img')).some((image) => image.getAttribute('src')?.trim());
  return text || hasImage ? sanitized : '';
}
